import React, { useState } from "react";
import classNames from "classnames";

import InfoDropdown from "components/InfoDropdown";

import { buildCommunityReadinessChecks } from "utils/communities/communityReadiness";

import classes from "./styles.module.scss";
import configClasses from "../style.module.scss";

const statusIcon = (status) => {
    if (status === "pass") {
        return "✓";
    }
    if (status === "fail") {
        return "✗";
    }
    return "…";
};

const ReadinessChecklist = ({
    hasMissingCodes,
    ecmTableSyncIssues = [],
    measureStats = {},
    validationComboOutOfRange,
    insufficientRuns,
    validationInProgress,
    failingLabels = [],
    unvalidatedLabels = [],
    ecmSyncIssueDescription,
}) => {
    const [expanded, setExpanded] = useState(true);

    const checks = buildCommunityReadinessChecks({
        hasMissingCodes,
        ecmTableSyncIssues,
        measureStats,
        validationComboOutOfRange,
        insufficientRuns,
        validationInProgress,
    });

    const failingChecks = checks.filter(({ status }) => status !== "pass");
    const hasFailureDetails =
        failingLabels.length > 0 || unvalidatedLabels.length > 0 || ecmTableSyncIssues.length > 0;
    const renderCheckItem = (check) => (
        <li
            key={check.id}
            className={classNames(classes.checklistItem, {
                [classes.pass]: check.status === "pass",
                [classes.fail]: check.status === "fail",
                [classes.pending]: check.status === "pending",
            })}
        >
            <span className={classes.icon} aria-hidden>
                {statusIcon(check.status)}
            </span>
            <div className={classes.itemBody}>
                <span className={classes.label}>{check.label}</span>
                {check.detail ? <span className={classes.detail}>{check.detail}</span> : null}
            </div>
        </li>
    );

    return (
        <section className={classes.checklistSection} id="community-readiness-checklist">
            <div className={classes.checklistHeader}>
                <h3 className={configClasses.title}>Readiness checklist</h3>
                {failingChecks.length > 0 ? (
                    <button type="button" className={classes.toggleDetails} onClick={() => setExpanded((v) => !v)}>
                        {expanded ? "Hide details" : "Show details"}
                    </button>
                ) : null}
            </div>

            <div
                className={classNames(classes.checklistLayout, {
                    [classes.checklistLayoutWithDetails]: expanded && hasFailureDetails,
                })}
            >
                <ul className={classes.checklist}>
                    {checks.map((check) =>
                        renderCheckItem({
                            ...check,
                            detail: check.status !== "pass" && expanded ? check.detail : null,
                        })
                    )}
                </ul>

                {expanded && hasFailureDetails ? (
                    <div className={classes.failurePanel}>
                        <p className={classes.failurePanelTitle}>Details</p>
                        {failingLabels.length > 0 ? (
                            <InfoDropdown
                                className={classes.measureDropdown}
                                isError
                                label={`${failingLabels.length} measure(s) failed`}
                            >
                                <ul className={configClasses.failedMeasureList}>
                                    {failingLabels.map((label, idx) => (
                                        <li key={`${label}-${idx}`}>{label}</li>
                                    ))}
                                </ul>
                            </InfoDropdown>
                        ) : null}

                        {unvalidatedLabels.length > 0 ? (
                            <InfoDropdown
                                className={classes.measureDropdown}
                                isWarning
                                label={`${unvalidatedLabels.length} upgrade(s) not yet validated`}
                            >
                                <ul className={configClasses.failedMeasureList}>
                                    {unvalidatedLabels.map((label, idx) => (
                                        <li key={`unvalidated-${label}-${idx}`}>{label}</li>
                                    ))}
                                </ul>
                            </InfoDropdown>
                        ) : null}

                        {ecmTableSyncIssues.length > 0 ? (
                            <InfoDropdown
                                className={classes.measureDropdown}
                                isWarning
                                label={`${ecmTableSyncIssues.length} upgrade(s) out of sync with ECM map`}
                                contentClassName={configClasses.codeWarningContent}
                            >
                                <ul className={configClasses.failedMeasureList}>
                                    {ecmTableSyncIssues.map((issue, idx) => (
                                        <li key={`ecm-sync-${issue.packageId}-${issue.upgradeId}-${idx}`}>
                                            {ecmSyncIssueDescription(issue)}
                                        </li>
                                    ))}
                                </ul>
                            </InfoDropdown>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </section>
    );
};

export default ReadinessChecklist;
